import type { FeedPost, TopicItem, TreeholePost } from "../types/content";

export const API_BASE_URL = "https://z.zpai.cloud";

export function resolveAssetUrl(url: string): string {
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  return `${API_BASE_URL}${url.startsWith("/") ? url : `/${url}`}`;
}

export async function fetchSiteSettings() {
  return request<Record<string, string>>("/api/site-settings");
}
const TOKEN_KEY = "bigbluebook_token";

/// Simple in-memory cache with TTL for non-mutating API responses.
const cache = new Map<string, { data: unknown; expiresAt: number }>();
function getCached<T>(key: string): T | undefined {
  const entry = cache.get(key);
  if (entry && entry.expiresAt > Date.now()) return entry.data as T;
  cache.delete(key);
  return undefined;
}
function setCache(key: string, data: unknown, ttlMs = 30_000) {
  cache.set(key, { data, expiresAt: Date.now() + ttlMs });
}
// unused export kept for future use
// function invalidateCache(prefix?: string) { ... }

export interface ApiUser {
  readonly id: string;
  readonly nickname: string;
  readonly email?: string | null;
  readonly phone?: string | null;
  readonly avatar?: string | null;
  readonly bio?: string | null;
  readonly city?: string | null;
  readonly occupation?: string | null;
  readonly receivedLikes?: number;
  readonly _count?: {
    readonly posts?: number;
    readonly followers?: number;
    readonly following?: number;
  };
}

export interface ApiTopic {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly description?: string | null;
  readonly cover?: string | null;
  readonly icon?: string | null;
  readonly postCount: number;
}

export interface ApiBoard {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly kind: "scene" | "board";
  readonly icon?: string | null;
  readonly postCount: number;
  readonly sortOrder: number;
}

export interface ApiPostMedia {
  readonly id: string;
  readonly url: string;
  readonly mediaType: string;
  readonly alt?: string | null;
  readonly sortOrder: number;
}

export interface ApiPost {
  readonly id: string;
  readonly title: string;
  readonly content: string;
  readonly postType: string;
  readonly isAnonymous: boolean;
  readonly createdAt: string;
  readonly author: ApiUser | null;
  readonly media?: readonly ApiPostMedia[];
  readonly counts: {
    readonly comments: number;
    readonly likes: number;
    readonly favorites: number;
  };
  readonly topics: readonly ApiTopic[];
  readonly viewerState?: {
    readonly liked: boolean;
    readonly favorited: boolean;
  };
}

export interface ApiGuide {
  readonly id: string;
  readonly title: string;
  readonly summary: string;
  readonly category: string;
  readonly sourcePostId?: string | null;
  readonly sourcePost?: {
    readonly id: string;
  } | null;
}

interface RequestOptions extends RequestInit {
  readonly auth?: boolean;
}

export interface PublishPayload {
  readonly title: string;
  readonly content: string;
  readonly postType: "normal" | "treehole" | "qa" | "guide";
  readonly topicIds?: readonly string[];
  readonly boardId?: string;
  readonly isAnonymous?: boolean;
  readonly mediaUrls?: readonly string[];
}

export interface ProfileView {
  readonly name: string;
  readonly id: string;
  readonly avatar?: string | null;
  readonly bio: string;
  readonly stats: readonly { readonly label: string; readonly value: string }[];
  readonly city?: string | null;
  readonly occupation?: string | null;
}

export interface ApiCoverageItem {
  readonly method: string;
  readonly path: string;
  readonly implemented: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("content-type", "application/json");

  if (options.auth) {
    const token = getStoredToken();
    if (!token) {
      throw new Error("AUTH_REQUIRED");
    }
    headers.set("authorization", `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
  });

  const text = await response.text();
  const body = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new ApiError(
      body?.error || `${response.status} ${response.statusText}`,
      response.status,
      body?.requestId,
    );
  }

  return body as T;
}

export class ApiError extends Error {
  readonly status: number;
  readonly requestId?: string;
  constructor(message: string, status: number, requestId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.requestId = requestId;
  }
}

function getOptionalAuthHeaders() {
  const token = getStoredToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers["authorization"] = `Bearer ${token}`;
  }
  return { headers };
}

export function getStoredToken() {
  return window.localStorage.getItem(TOKEN_KEY);
}

export function clearAuthToken() {
  window.localStorage.removeItem(TOKEN_KEY);
}

function saveAuthToken(token: string) {
  window.localStorage.setItem(TOKEN_KEY, token);
}

function authIdentifier(value: string): { email: string } | { phone: string } {
  const identifier = value.trim();
  return identifier.includes("@")
    ? { email: identifier.toLowerCase() }
    : { phone: identifier };
}

export async function login(identifier: string, password: string) {
  const result = await request<{ token: string; user: ApiUser }>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ ...authIdentifier(identifier), password }),
  });
  saveAuthToken(result.token);
  return result;
}

export async function register(identifier: string, password: string, nickname: string) {
  const result = await request<{ token: string; user: ApiUser }>("/api/auth/register", {
    method: "POST",
    body: JSON.stringify({ ...authIdentifier(identifier), password, nickname }),
  });
  saveAuthToken(result.token);
  return result;
}

export type SmsCodePurpose = "register" | "login";

export async function sendSmsCode(phone: string, purpose: SmsCodePurpose) {
  return request<{ expiresIn: number; resendAfter: number; testCode?: string }>("/api/auth/sms/send", {
    method: "POST",
    body: JSON.stringify({ phone: phone.trim(), purpose }),
  });
}

export async function registerPhone(phone: string, code: string, nickname?: string) {
  const result = await request<{ token: string; user: ApiUser; needsPasswordSetup: boolean }>("/api/auth/register/phone", {
    method: "POST",
    body: JSON.stringify({ phone: phone.trim(), code: code.trim(), nickname: nickname?.trim() || undefined }),
  });
  saveAuthToken(result.token);
  return result;
}

export async function loginWithPhoneCode(phone: string, code: string) {
  const result = await request<{ token: string; user: ApiUser }>("/api/auth/login/phone-code", {
    method: "POST",
    body: JSON.stringify({ phone: phone.trim(), code: code.trim() }),
  });
  saveAuthToken(result.token);
  return result;
}

export async function setPhonePassword(password: string) {
  return request<{ user: ApiUser; passwordSet: boolean }>("/api/auth/phone/password", {
    method: "POST",
    auth: true,
    body: JSON.stringify({ password }),
  });
}

function formatCount(count: number) {
  if (count >= 10000) return `${(count / 10000).toFixed(1)}w`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return String(count);
}

function mapPostToFeedPost(post: ApiPost, index = 0): FeedPost {
  const rawImage = post.media?.[0]?.url;
  const rawAvatar = post.author?.avatar;
  return {
    id: post.id,
    title: post.title,
    author: post.author?.nickname || "匿名散帅",
    authorId: post.author?.id,
    authorAvatar: rawAvatar ? resolveAssetUrl(rawAvatar) : undefined,
    likes: formatCount(post.counts.likes),
    image: rawImage ? resolveAssetUrl(rawImage) : "",
    comments: formatCount(post.counts.comments),
    favorites: formatCount(post.counts.favorites),
    accent: index % 3 === 0 ? "blue" : index % 3 === 1 ? "lavender" : "peach",
    distance: post.author?.city ? `${post.author.city}` : undefined,
    liked: post.viewerState?.liked,
    favorited: post.viewerState?.favorited,
  };
}

function mapPostToTreeholePost(post: ApiPost): TreeholePost {
  const firstTopic = post.topics[0]?.name;
  const rawImage = post.media?.[0]?.url;
  const rawAvatar = post.author?.avatar;

  return {
    id: post.id,
    author: post.author?.nickname || "匿名散帅",
    authorId: post.author?.id,
    authorAvatar: rawAvatar ? resolveAssetUrl(rawAvatar) : undefined,
    time: "刚刚",
    tag: firstTopic ? `#${firstTopic}` : undefined,
    content: post.content,
    resonance: `${formatCount(post.counts.likes)} 共鸣`,
    comments: String(post.counts.comments),
    image: rawImage ? resolveAssetUrl(rawImage) : undefined,
    action: "共鸣一下",
    liked: post.viewerState?.liked,
    favorited: post.viewerState?.favorited,
  };
}

export async function fetchHomeFeed() {
  const result = await request<{ items: ApiPost[] }>("/api/feed", getOptionalAuthHeaders());
  return result.items.map(mapPostToFeedPost);
}

export async function fetchFollowingFeed() {
  const result = await request<{ items: ApiPost[] }>("/api/feed/following", { auth: true });
  return result.items.map(mapPostToFeedPost);
}

export async function fetchNearbyFeed(city?: string) {
  const token = getStoredToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers["authorization"] = `Bearer ${token}`;
  }
  const query = city ? `?city=${encodeURIComponent(city)}` : "";
  const result = await request<{ items: ApiPost[] }>(`/api/feed/nearby${query}`, { headers });
  return result.items.map(mapPostToFeedPost);
}

export async function fetchTopics(): Promise<TopicItem[]> {
  const cached = getCached<TopicItem[]>("topics");
  if (cached) return cached;
  const result = await request<{ items: ApiTopic[] }>("/api/topics");
  const items = result.items.map((topic): TopicItem => ({
    id: topic.id,
    title: topic.name.startsWith("#") ? topic.name : `# ${topic.name}`,
    subtitle: topic.description || "社区热议主题",
    metric: `${formatCount(topic.postCount)} 篇内容`,
    image: topic.cover || "",
  }));
  setCache("topics", items);
  return items;
}

export async function fetchBoards(kind?: ApiBoard["kind"]): Promise<ApiBoard[]> {
  const cacheKey = `boards:${kind || "all"}`;
  const cached = getCached<ApiBoard[]>(cacheKey);
  if (cached) return cached;
  const result = await request<{ items: ApiBoard[] }>(`/api/boards${kind ? `?kind=${kind}` : ""}`);
  setCache(cacheKey, result.items);
  return result.items;
}

export async function fetchBoardDetail(boardId: string): Promise<ApiBoard> {
  const result = await request<{ board: ApiBoard }>(`/api/boards/${boardId}`);
  return result.board;
}

export async function fetchBoardPosts(boardId: string): Promise<ApiPost[]> {
  const result = await request<{ items: ApiPost[] }>(`/api/boards/${boardId}/posts`);
  return result.items;
}

export async function fetchTreeholePosts() {
  const result = await request<{ items: ApiPost[] }>("/api/posts?postType=treehole", getOptionalAuthHeaders());
  return result.items.map(mapPostToTreeholePost);
}

export async function fetchOnlineCount(): Promise<number> {
  const cached = getCached<number>("online-count");
  if (cached) return cached;
  const result = await request<{ count: number }>("/api/stats/online");
  setCache("online-count", result.count, 10_000);
  return result.count;
}

export function getNormalizedAvatar(avatarUrl: string | null | undefined): string {
  if (!avatarUrl) {
    return `${API_BASE_URL}/uploads/default-avatar.jpg`;
  }
  if (avatarUrl === "/default-avatar.jpg" || avatarUrl.endsWith("/default-avatar.jpg")) {
    return `${API_BASE_URL}/uploads/default-avatar.jpg`;
  }
  return avatarUrl;
}

export async function fetchMe() {
  const result = await request<{ user: ApiUser }>("/api/users/me", { auth: true });
  const user = result.user;

  return {
    name: user.nickname,
    id: user.id,
    avatar: getNormalizedAvatar(user.avatar),
    bio: user.bio || "这个人还没有填写简介。",
    city: user.city,
    occupation: user.occupation,
    stats: [
      { label: "关注", value: formatCount(user._count?.following || 0) },
      { label: "粉丝", value: formatCount(user._count?.followers || 0) },
      { label: "获赞", value: formatCount(user.receivedLikes || 0) },
    ],
  } satisfies ProfileView;
}

export async function fetchMyPosts(userId: string) {
  const result = await request<{ items: ApiPost[] }>(`/api/users/${userId}/posts`, getOptionalAuthHeaders());
  return result.items.map((post, index) => mapPostToFeedPost(post, index));
}

export async function fetchMyLikedPosts() {
  const result = await request<{ items: ApiPost[] }>("/api/users/me/liked", { auth: true });
  return result.items.map((post, index) => mapPostToFeedPost(post, index));
}

export async function fetchMySavedPosts() {
  const result = await request<{ items: ApiPost[] }>("/api/users/me/saved", { auth: true });
  return result.items.map((post, index) => mapPostToFeedPost(post, index));
}

export async function publishPost(payload: PublishPayload) {
  const result = await request<{ post: ApiPost }>("/api/posts", {
    method: "POST",
    auth: true,
    body: JSON.stringify(payload),
  });
  return result.post;
}

export async function likePost(postId: string) {
  await request(`/api/posts/${postId}/likes`, { method: "POST", auth: true });
}

export async function fetchGuides(): Promise<ApiGuide[]> {
  const cached = getCached<ApiGuide[]>("guides");
  if (cached) return cached;
  const result = await request<{ items: ApiGuide[] }>("/api/guides");
  const items = result.items;
  setCache("guides", items);
  return items;
}

export async function checkApiHealth() {
  return request<{ ok: boolean; service: string }>("/api/health");
}

export async function fetchPostDetail(postId: string) {
  const result = await request<{ post: ApiPost }>(`/api/posts/${postId}`, getOptionalAuthHeaders());
  const post = result.post;
  return {
    ...post,
    author: post.author ? {
      ...post.author,
      avatar: getNormalizedAvatar(post.author.avatar)
    } : null
  };
}

export async function unlikePost(postId: string) {
  await request(`/api/posts/${postId}/likes`, { method: "DELETE", auth: true });
}

export async function favoritePost(postId: string) {
  await request(`/api/posts/${postId}/favorites`, { method: "POST", auth: true });
}

export async function unfavoritePost(postId: string) {
  await request(`/api/posts/${postId}/favorites`, { method: "DELETE", auth: true });
}

export interface ApiComment {
  readonly id: string;
  readonly content: string;
  readonly createdAt: string;
  readonly author: ApiUser;
  readonly parentId?: string | null;
  readonly replies?: readonly ApiComment[];
  readonly counts?: {
    readonly likes: number;
  };
  readonly viewerState?: {
    readonly liked: boolean;
  };
}

export async function fetchPostComments(postId: string) {
  const result = await request<{ items: ApiComment[] }>(`/api/posts/${postId}/comments`, getOptionalAuthHeaders());
  return result.items.map((comment) => ({
    ...comment,
    author: {
      ...comment.author,
      avatar: getNormalizedAvatar(comment.author.avatar),
    },
    replies: comment.replies?.map((reply) => ({
      ...reply,
      author: {
        ...reply.author,
        avatar: getNormalizedAvatar(reply.author.avatar),
      },
    })),
  }));
}

export async function createComment(postId: string, content: string, parentId?: string) {
  const result = await request<{ comment: ApiComment }>(`/api/posts/${postId}/comments`, {
    method: "POST",
    auth: true,
    body: JSON.stringify({ content, parentId }),
  });
  return result.comment;
}

export async function likeComment(commentId: string) {
  await request(`/api/comments/${commentId}/likes`, { method: "POST", auth: true });
}

export async function unlikeComment(commentId: string) {
  await request(`/api/comments/${commentId}/likes`, { method: "DELETE", auth: true });
}

export async function fetchTopicDetail(topicId: string): Promise<ApiTopic> {
  const cached = getCached<ApiTopic>("topic:" + topicId);
  if (cached) return cached;
  const result = await request<{ topic: ApiTopic }>(`/api/topics/${topicId}`);
  setCache("topic:" + topicId, result.topic, 60_000);
  return result.topic;
}

export async function fetchTopicPosts(topicId: string) {
  const result = await request<{ items: ApiPost[] }>(`/api/topics/${topicId}/posts`, getOptionalAuthHeaders());
  return result.items;
}

export async function fetchUserDetail(userId: string) {
  const result = await request<{ user: ApiUser }>(`/api/users/${userId}`, getOptionalAuthHeaders());
  const user = result.user;
  return {
    ...user,
    avatar: getNormalizedAvatar(user.avatar)
  };
}

export async function followUser(userId: string) {
  const result = await request<{ follow: unknown }>("/api/follows", {
    method: "POST",
    auth: true,
    body: JSON.stringify({ userId }),
  });
  return result.follow;
}

export async function unfollowUser(userId: string) {
  await request(`/api/follows/${userId}`, { method: "DELETE", auth: true });
}

export async function fetchFollowStatus(userId: string) {
  const result = await request<{ following: boolean; status: string | null }>(`/api/follows/status?userId=${userId}`, { auth: true });
  return result;
}

export interface ApiConversation {
  readonly id: string;
  readonly type: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly members: readonly {
    readonly id: string;
    readonly user: ApiUser;
  }[];
}

export interface ApiMessage {
  readonly id: string;
  readonly senderId: string;
  readonly content: string;
  readonly createdAt: string;
  readonly sender: ApiUser;
}

export async function createConversation(memberIds: string[], type = "direct") {
  const result = await request<{ conversation: ApiConversation }>("/api/conversations", {
    method: "POST",
    auth: true,
    body: JSON.stringify({ memberIds, type }),
  });
  return result.conversation;
}

export async function fetchConversations() {
  const result = await request<{ items: ApiConversation[] }>("/api/conversations", { auth: true });
  return result.items;
}

export async function fetchConversationMessages(convId: string) {
  const result = await request<{ items: ApiMessage[] }>(`/api/conversations/${convId}/messages`, { auth: true });
  return result.items;
}

export async function sendConversationMessage(convId: string, content: string) {
  const result = await request<{ message: ApiMessage }>(`/api/conversations/${convId}/messages`, {
    method: "POST",
    auth: true,
    body: JSON.stringify({ content }),
  });
  return result.message;
}

export async function searchAll(query: string) {
  const result = await request<{
    readonly posts: readonly ApiPost[];
    readonly topics: readonly ApiTopic[];
    readonly guides: readonly ApiGuide[];
  }>(`/api/search?q=${encodeURIComponent(query)}`, getOptionalAuthHeaders());
  return result;
}

export async function updateUserProfile(payload: {
  nickname?: string;
  avatar?: string | null;
  gender?: string | null;
  ageRange?: string | null;
  city?: string | null;
  occupation?: string | null;
  bio?: string | null;
  tags?: { tag: string; tagType: string }[];
}) {
  const result = await request<{ user: ApiUser }>("/api/users/me", {
    method: "PATCH",
    auth: true,
    body: JSON.stringify(payload),
  });
  return result.user;
}

export async function fetchUserFollowers(userId: string) {
  const result = await request<{ items: ApiUser[] }>(`/api/users/${userId}/followers`);
  return result.items.map((u) => ({
    ...u,
    avatar: getNormalizedAvatar(u.avatar),
  }));
}

export async function fetchUserFollowing(userId: string) {
  const result = await request<{ items: ApiUser[] }>(`/api/users/${userId}/following`);
  return result.items.map((u) => ({
    ...u,
    avatar: getNormalizedAvatar(u.avatar),
  }));
}

export async function updatePost(postId: string, payload: Partial<PublishPayload>) {
  const result = await request<{ post: ApiPost }>(`/api/posts/${postId}`, {
    method: "PATCH",
    auth: true,
    body: JSON.stringify(payload),
  });
  return result.post;
}

export async function deletePost(postId: string) {
  await request(`/api/posts/${postId}`, { method: "DELETE", auth: true });
}

export async function fetchGuideDetail(guideId: string) {
  const result = await request<{ guide: ApiGuide }>(`/api/guides/${guideId}`);
  return result.guide;
}

export async function createGuideFromPost(postId: string, title?: string, summary?: string, category?: string) {
  const result = await request<{ guide: ApiGuide }>("/api/guides/from-post", {
    method: "POST",
    auth: true,
    body: JSON.stringify({ postId, title, summary, category }),
  });
  return result.guide;
}

export async function fetchRecommendedUsers(): Promise<ApiUser[]> {
  const cached = getCached<ApiUser[]>("recommended-users");
  if (cached) return cached;
  const result = await request<{ items: ApiUser[] }>("/api/recommend/users");
  const items = result.items.map(u => ({
    ...u,
    avatar: u.avatar ? resolveAssetUrl(u.avatar) : u.avatar,
  }));
  setCache("recommended-users", items, 60_000);
  return items;
}

export async function fetchRecommendedTopics(): Promise<ApiTopic[]> {
  const cached = getCached<ApiTopic[]>("recommended-topics");
  if (cached) return cached;
  const result = await request<{ items: ApiTopic[] }>("/api/recommend/topics");
  const items = result.items;
  setCache("recommended-topics", items, 60_000);
  return items;
}

export async function reportContent(targetType: "post" | "comment" | "guide" | "user", targetId: string, reason: string) {
  const result = await request<{ report: unknown }>("/api/reports", {
    method: "POST",
    auth: true,
    body: JSON.stringify({ targetType, targetId, reason }),
  });
  return result.report;
}

export async function blockUser(userId: string) {
  const result = await request<{ block: unknown }>("/api/blocks", {
    method: "POST",
    auth: true,
    body: JSON.stringify({ userId }),
  });
  return result.block;
}

export async function unblockUser(userId: string) {
  const result = await request<{ success: boolean }>(`/api/blocks/${userId}`, {
    method: "DELETE",
    auth: true,
  });
  return result.success;
}

export interface ContactRequestItem {
  readonly id: string;
  readonly message?: string;
  readonly requester: ApiUser;
  readonly status: string;
}

export async function sendContactRequest(receiverId: string, message?: string) {
  const result = await request<{ request: unknown }>("/api/contact-requests", {
    method: "POST",
    auth: true,
    body: JSON.stringify({ receiverId, message }),
  });
  return result.request;
}

export async function fetchContactRequests() {
  const result = await request<{ items: ContactRequestItem[] }>("/api/contact-requests", { auth: true });
  return result.items;
}

export async function approveContactRequest(requestId: string) {
  const result = await request<{ conversation: ApiConversation }>(`/api/contact-requests/${requestId}/approve`, {
    method: "POST",
    auth: true,
  });
  return result;
}

export async function fetchBlockedUsers() {
  const result = await request<{ items: ApiUser[] }>("/api/blocks", { auth: true });
  return result.items.map((u) => ({
    ...u,
    avatar: getNormalizedAvatar(u.avatar),
  }));
}

export async function uploadFile(file: File) {
  const formData = new FormData();
  formData.append("file", file);

  const token = getStoredToken();
  if (!token) {
    throw new Error("AUTH_REQUIRED");
  }

  const headers = new Headers();
  headers.set("authorization", `Bearer ${token}`);

  const response = await fetch(`${API_BASE_URL}/api/upload`, {
    method: "POST",
    headers,
    body: formData,
  });

  const text = await response.text();
  const body = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new ApiError(
      body?.error || `${response.status} ${response.statusText}`,
      response.status,
      body?.requestId,
    );
  }

  return body.file as { originalName: string; mimeType: string; size: number; url: string };
}

export const expectedApiCoverage: readonly ApiCoverageItem[] = [
  ["POST", "/api/auth/register"],
  ["POST", "/api/auth/login"],
  ["GET", "/api/users/me"],
  ["PATCH", "/api/users/me"],
  ["GET", "/api/users/:id"],
  ["GET", "/api/users/:id/posts"],
  ["GET", "/api/users/:id/followers"],
  ["GET", "/api/users/:id/following"],
  ["GET", "/api/topics"],
  ["GET", "/api/topics/:id"],
  ["GET", "/api/topics/:id/posts"],
  ["POST", "/api/posts"],
  ["GET", "/api/posts"],
  ["GET", "/api/posts/:id"],
  ["PATCH", "/api/posts/:id"],
  ["DELETE", "/api/posts/:id"],
  ["POST", "/api/posts/:id/comments"],
  ["GET", "/api/posts/:id/comments"],
  ["POST", "/api/comments/:id/likes"],
  ["DELETE", "/api/comments/:id/likes"],
  ["POST", "/api/posts/:id/likes"],
  ["DELETE", "/api/posts/:id/likes"],
  ["POST", "/api/posts/:id/favorites"],
  ["DELETE", "/api/posts/:id/favorites"],
  ["GET", "/api/guides"],
  ["GET", "/api/guides/:id"],
  ["POST", "/api/guides/from-post"],
  ["POST", "/api/follows"],
  ["DELETE", "/api/follows/:userId"],
  ["GET", "/api/follows/status"],
  ["POST", "/api/contact-requests"],
  ["GET", "/api/contact-requests"],
  ["POST", "/api/conversations"],
  ["GET", "/api/conversations"],
  ["GET", "/api/conversations/:id/messages"],
  ["POST", "/api/conversations/:id/messages"],
  ["GET", "/api/feed"],
  ["GET", "/api/search"],
  ["GET", "/api/stats/online"],
  ["GET", "/api/recommend/users"],
  ["GET", "/api/recommend/topics"],
  ["POST", "/api/reports"],
  ["POST", "/api/blocks"],
  ["GET", "/api/blocks"],
  ["POST", "/api/upload"],
].map(([method, path]) => ({ method, path, implemented: true }));
